import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/* ===========================================================================
   SIMONE SLIDE CART
   Drop-in replacement for assets/cart-drawer.js

   ONE-TIME PURCHASE. No selling plans are sent, no recurring terms are shown,
   and nothing in here promises a future charge.
   =========================================================================== */

/* ------------------------------- CONFIG ---------------------------------- */

/* THE ONLY PRICE YOU SET.
   REG_BOTTLE is the regular price of ONE bottle, in cents, and must equal the
   PDP's `REG` (currently 59). */
const REG_BOTTLE = 5900;

/* Free shipping threshold, in cents. Must match the automatic discount in
   Shopify (Discounts > "Free shipping over $80", minimum purchase $80).
   Set to 999999 to hide the bar entirely. */
const FREE_SHIP = 8000;

/* One place for the social-proof figure. Must match the PDP star bar. */
const REVIEW_COUNT = '3,200+';

/* THE LADDER - this must mirror the PDP's TIERS.
   The old rule was "one free per paid bottle", which quietly invented a
   Buy 1 Get 1 tier the offer does not sell: drop from 2 paid to 1 and the
   drawer handed out a free bottle at $49. free is what a given PAID count
   entitles, and a count above the top tier earns the top tier's gifts, never
   more. Change the offer, change this table and the PDP together. */
const TIERS = [
  { paid: 1, free: 0 },
  { paid: 2, free: 2 },
];

function freeFor(paid) {
  let earned = 0;
  for (const t of TIERS) { if (paid >= t.paid) earned = t.free; }
  return earned;
}

const SCENTS = [
  { n: 'Sugared Skin',  paid: 45960049983590, free: 45971585269862 },
  { n: 'Vanilla Noir',  paid: 45960049885286, free: 45971585171558 },
  { n: 'Wild Honey',    paid: 45960049918054, free: 45971585204326 },
  { n: 'Bloom & Blush', paid: 45960049950822, free: 45971585237094 },
];

const PAID = {};
const FREE = {};
for (const s of SCENTS) { PAID[s.paid] = s; FREE[s.free] = s; }

/* THE BUNDLE HANDLES.
   Classification goes by product handle, so it works for every bundle product
   and any new one you add later, with no variant ids to maintain.

   NOTE: 'desire-oil-bunde' is a real handle with a real typo in it. The
   product title reads "Desire Oil Bunde" on every cart line, at checkout and
   in the confirmation email. Worth renaming - and if you do, add the new
   handle here BEFORE you rename, or the gifts will vanish for anyone whose
   cart is mid-flight. */
/* The one-time split-test product. A cart built from it resets to a single
   bottle of ITSELF, never of desire-oil-single, so this traffic stays on one
   listing end to end. Carts built from the Seal products are untouched by the
   reset path below. */
const BUNDLE_HANDLE = 'desire-oil-bunde';

const FREE_HANDLE  = 'desire-oil-free';
const PAID_HANDLES = ['desire-oil', 'desire-oil-b2g2', 'desire-oil-b3g3',
                      'desire-oil-bunde', 'desire-oil-bundle', 'desire-oil-single'];

/* RITUAL ADD-ONS. Not bottles, so the gift maths never counts them. Regular
   prices in cents, so the footer and the line cards can strike them the same
   way the PDP does. GIFT_HANDLES render as FREE with no stepper. */
const ADDON_REG = {
  'desire-butter':           4000,
  'desire-parfum-10ml-free': 2000,
  'desire-parfum-10ml':      2000,
};
const GIFT_HANDLES = ['desire-parfum-10ml-free'];
/* The parfum is earned by the butter: exactly one per order while a butter
   is in the cart, none when it is not. It is a $0 line, so nothing else ever
   corrects it - without this, a second click on the PDP doubled it. */
const BUTTER_HANDLE = 'desire-butter';
const GIFT_VARIANT  = 47202348138598;
/* The one-click upgrade card shown on a B2G2 cart: add the butter, and the
   parfum + free shipping follow (giftFix adds the parfum, the $80 rule does
   the shipping). BUTTER_PRICE is what the butter variant is priced at. */
const BUTTER_VARIANT = 46552059084902;
const BUTTER_PRICE   = 3000;
const CDN = 'https://cdn.shopify.com/s/files/1/0698/4849/8278/files/';
const BUTTER_IMG = CDN + 'hf_20260810_011019_fc77484f-f664-4ae1-9b48-5e428fb4ed4b_200x.png';
const PARFUM_IMG = CDN + 'hf_20260810_010926_6bb87649-f301-47c6-910e-c0cd38186ea6_200x.png';

/* 1-BOTTLE -> B2G2 UPGRADE. The single bottle ($49) is its own product, so
   the upgrade swaps it for a bundle-priced bottle of the SAME scent plus one
   more scent; balance() then adds the two gifts. Order matches the PDP. */
const SINGLE_TO_BUNDLE = [
  { single: 47208562884710, paid: 47208827813990, n: 'Sugared Skin'  },
  { single: 47208562786406, paid: 47208827715686, n: 'Vanilla Noir'  },
  { single: 47208562819174, paid: 47208827748454, n: 'Wild Honey'    },
  { single: 47208562851942, paid: 47208827781222, n: 'Bloom & Blush' },
];
const B2G2_PRICE = 5900;
const BOTTLE_IMGS = [
  CDN + 'SUGAREDSKIN4OZBODYOIL_200x.png',
  CDN + 'VANILLANOIR4OZBODYOIL_200x.png',
];

function upgradeToBundle() {
  const line = (cart.items || []).find(isPaidLine);
  if (!line) return Promise.resolve();
  swapping = true;
  let idx = SINGLE_TO_BUNDLE.findIndex((x) => x.single === line.variant_id || x.paid === line.variant_id);
  if (idx < 0) idx = SINGLE_TO_BUNDLE.findIndex((x) => x.n === line.variant_title);
  if (idx < 0) idx = 0;
  const same  = SINGLE_TO_BUNDLE[idx].paid;
  const other = SINGLE_TO_BUNDLE[(idx + 1) % SINGLE_TO_BUNDLE.length].paid;
  const isSingle = line.variant_id !== same;
  const step = isSingle
    ? post('/cart/update.js', { updates: { [line.variant_id]: 0 } }).then(() => addOne({ id: same, quantity: 1 }))
    : Promise.resolve();
  return step
    .then(() => addOne({ id: other, quantity: 1 }))
    .then(() => { swapping = false; })
    .catch((e) => { swapping = false; console.error('[simone cart] upgrade failed', e); });
}
function isGiftLine(l) { return GIFT_HANDLES.indexOf(l.handle) !== -1; }
function addonReg(l)   { return ADDON_REG[l.handle] || 0; }
const stripFree = (t) => String(t || '').replace(/\s*\(free\)\s*$/i, '');

function isFreeLine(l) {
  return l.handle === FREE_HANDLE || !!FREE[l.variant_id];
}
function isPaidLine(l) {
  if (isFreeLine(l)) return false;
  return PAID_HANDLES.indexOf(l.handle) !== -1 || !!PAID[l.variant_id];
}

const money = (c) => '$' + (c / 100).toFixed(2);

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/* --------------------------- product variant cache ------------------------ */
const PRODUCTS = {};
const PRODUCT_JOBS = {};

function loadProduct(handle) {
  if (PRODUCTS[handle]) return Promise.resolve(PRODUCTS[handle]);
  if (PRODUCT_JOBS[handle]) return PRODUCT_JOBS[handle];

  PRODUCT_JOBS[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((p) => {
      PRODUCTS[handle] = ((p && p.variants) || []).map((v) => ({
        id: v.id,
        title: v.title,
        available: v.available !== false,
        img: (v.featured_image && v.featured_image.src) || '',
      }));
      delete PRODUCT_JOBS[handle];
      return PRODUCTS[handle];
    })
    .catch((e) => {
      console.warn('[simone cart] could not load /products/' + handle + '.js', e);
      PRODUCTS[handle] = [];
      delete PRODUCT_JOBS[handle];
      return PRODUCTS[handle];
    });

  return PRODUCT_JOBS[handle];
}

function loadCartProducts() {
  const handles = [];
  for (const l of cart.items || []) {
    if (l.handle && handles.indexOf(l.handle) === -1) handles.push(l.handle);
  }
  return Promise.all(handles.map(loadProduct));
}

let cart = null;
let busy = false;
let reconciling = false;
/* True while a scent swap is mid-flight. A swap is a remove followed by an
   add, and between those two calls the cart is short one bottle - exactly the
   state that used to make reconcile() delete the gifts. */
let swapping = false;
/* Bounded so a rebalance can never loop forever. */
let autoPass = 0;
/* window.simoneCart.trace(true)  ->  log every balance decision. */
let trace = false;
let panel = null;
let overlay = null;
/* True when the cart is on the gift tier. Set once per render. */
let onBundle = false;

/* --------------------------- reservation timer ---------------------------- */
const RESERVE_MINUTES = 10;
let reserveEnd = null;
let reserveTick = null;

function startReserve() {
  const now = Date.now();
  let stored = null;
  try { stored = sessionStorage.getItem('sc-reserve'); } catch (e) { /* private mode */ }

  reserveEnd = (stored && +stored > now) ? +stored : now + RESERVE_MINUTES * 60000;
  if (!stored || +stored <= now) {
    try { sessionStorage.setItem('sc-reserve', String(reserveEnd)); } catch (e) { /* no-op */ }
  }

  paintReserve();
  clearInterval(reserveTick);
  reserveTick = setInterval(paintReserve, 1000);
}

function paintReserve() {
  if (!panel) return;
  const box = ref('timer');
  const txt = ref('timertxt');
  if (!box || !txt) return;

  const left = Math.max(0, reserveEnd - Date.now());
  if (left === 0) {
    clearInterval(reserveTick);
    box.className = 'sc-timer done';
    txt.innerHTML = 'Your cart is saved &mdash; check out whenever you&rsquo;re ready';
    return;
  }
  const m = Math.floor(left / 60000);
  const sec = Math.floor((left % 60000) / 1000);
  box.className = 'sc-timer';
  txt.innerHTML = 'Your cart is reserved for <b>' + m + ':' + (sec < 10 ? '0' : '') + sec + '</b>';
}

/* --------------------------------- CSS ----------------------------------- */
const CSS = `
:root{--sc-ink:#141412;--sc-body:#3d3a35;--sc-mut:#6b6860;--sc-gold:#c19a3f;--sc-goldd:#9a7826;
--sc-cream:#faf3e3;--sc-cream2:#f6ead1;--sc-line:#e6e2db;--sc-soft:#f7f5f1}
.sc-ov{position:fixed;inset:0;background:rgba(18,14,10,.5);opacity:0;visibility:hidden;
transition:opacity .30s cubic-bezier(.4,0,1,1),visibility .30s;z-index:2147483646}
.sc-ov.on{opacity:1;visibility:visible;transition:opacity .40s cubic-bezier(.32,.72,0,1),visibility 0s}

@media (prefers-reduced-motion:reduce){
  .sc,.sc.on,.sc-ov,.sc-ov.on{transition-duration:.01ms}
}
/* iOS sheet curve: fast off the line, long decelerating tail. Closing is
   quicker because a dismissal should feel immediate, not ceremonial. */
.sc{position:fixed;top:0;right:0;bottom:0;width:100%;max-width:440px;background:#fff;z-index:2147483647;
display:flex;flex-direction:column;transform:translateX(100%);
transition:transform .30s cubic-bezier(.4,0,1,1);
font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:var(--sc-ink);
-webkit-font-smoothing:antialiased;box-shadow:-10px 0 44px rgba(18,14,10,.2)}
.sc.on{transform:translateX(0);transition:transform .40s cubic-bezier(.32,.72,0,1)}
.sc *,.sc-ov *{box-sizing:border-box;margin:0;padding:0}
.sc [hidden]{display:none!important}

/* header */
.sc-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 20px 8px;flex:none}
.sc-hd h2{font-size:19px;font-weight:800;letter-spacing:-.02em}
.sc-x{width:34px;height:34px;border:0;background:0;cursor:pointer;font-size:26px;line-height:1;color:var(--sc-ink);font-weight:300}

/* cart reservation timer */
.sc-timer{flex:none;background:#fff;border-bottom:1px solid var(--sc-line);
padding:0 20px 7px;text-align:center;font-size:11px;font-weight:600;
color:var(--sc-mut);letter-spacing:.01em}
.sc-timer svg{width:11px;height:11px;vertical-align:-1.5px;margin-right:5px;
stroke:var(--sc-goldd);fill:none;stroke-width:2.2;stroke-linecap:round}
.sc-timer b{color:var(--sc-goldd);font-weight:800;font-variant-numeric:tabular-nums}
.sc-timer.done{background:#fff;border-bottom-color:var(--sc-line);font-weight:600;color:var(--sc-mut)}

/* free shipping bar */
.sc-ship{flex:none;margin:8px 20px 2px;padding:10px 12px;border-radius:10px;background:var(--sc-cream);border:1px solid #ecdcb8;display:flex;gap:10px;align-items:center}
.sc-ship svg{width:22px;height:22px;flex:none;stroke:var(--sc-goldd);fill:none;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
.sc-ship-b{flex:1;min-width:0}
.sc-ship-t{font-size:13px;font-weight:800;color:var(--sc-ink);letter-spacing:-.015em;line-height:1.25}
.sc-ship-t b{color:var(--sc-goldd)}
.sc-ship-s{font-size:11.5px;font-weight:500;color:var(--sc-mut);line-height:1.3;margin-top:1px}
.sc-ship-bar{height:6px;border-radius:99px;background:#eadfc4;margin-top:7px;overflow:hidden}
.sc-ship-fill{height:100%;border-radius:99px;background:linear-gradient(90deg,#dcbd77,var(--sc-goldd));transition:width .5s cubic-bezier(.32,.72,0,1)}
.sc-ship.done .sc-ship-fill{width:100%!important}

/* line items */
.sc-scroll{flex:1;position:relative;min-height:132px;display:flex}
.sc-body{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch}
.sc-more{
  position:absolute;left:50%;bottom:10px;transform:translateX(-50%) translateY(5px);
  width:40px;height:40px;border-radius:50%;border:0;
  background:var(--sc-goldd);box-shadow:0 5px 16px rgba(122,94,24,.40);
  display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;
  opacity:0;pointer-events:none;transition:opacity .22s ease,transform .22s ease;z-index:3;
}
.sc-more.on{opacity:1;pointer-events:auto;transform:translateX(-50%) translateY(0);
animation:scNudge 1.6s ease-in-out infinite}
.sc-more:active{transform:translateX(-50%) scale(.94);animation:none}
.sc-more svg{width:19px;height:19px;stroke:#fff;fill:none;stroke-width:2.6;
stroke-linecap:round;stroke-linejoin:round}
@keyframes scNudge{0%,100%{transform:translateX(-50%) translateY(0)}
50%{transform:translateX(-50%) translateY(5px)}}
.sc-scroll::after{content:'';position:absolute;left:0;right:0;bottom:0;height:60px;
pointer-events:none;opacity:0;transition:opacity .22s ease;z-index:2;
background:linear-gradient(to top,#fff 12%,rgba(255,255,255,0))}
.sc-scroll.more::after{opacity:1}
@media(prefers-reduced-motion:reduce){.sc-more.on{animation:none}}
.sc-lines{padding:2px 20px}
.sc-li{display:flex;gap:10px;padding:7px 0;position:relative;align-items:flex-start}
.sc-li+.sc-li{border-top:1px solid var(--sc-line)}

/* ---- the gift card ---- */
.sc-li.free{
  background:linear-gradient(180deg,#fdf8ec,#faf3e3);
  border:1.5px solid #e6d3a3;border-radius:14px;
  padding:17px 10px 8px;margin:9px 0 2px;
}
.sc-li.free+.sc-li.free{margin-top:7px}
.sc-li+.sc-li.free{border-top:1.5px solid #e6d3a3}
.sc-li.free+.sc-li{border-top:none}
.sc-gifttag{
  position:absolute;top:-8px;left:10px;
  display:inline-flex;align-items:center;gap:4px;
  background:var(--sc-goldd);color:#fff;
  font-size:9px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;
  padding:4px 8px;border-radius:20px;line-height:1;white-space:nowrap;
  box-shadow:0 2px 6px rgba(122,94,24,.22);
}
.sc-gifttag svg{width:10px;height:10px;fill:currentColor}
.sc-li.free .sc-li-img{border:1px solid #ecdcb8;background:#fff}
.sc-li.free .sc-li-now.free{font-size:15px;letter-spacing:.02em}
.sc-li-img{width:46px;height:46px;border-radius:8px;overflow:hidden;background:var(--sc-soft);flex:none}
.sc-li-img img{width:100%;height:100%;object-fit:cover;display:block}
.sc-li-b{flex:1;min-width:0;padding-right:20px}
.sc-li-t{font-size:13px;font-weight:800;line-height:1.2;letter-spacing:-.015em}
.sc-li-s{font-size:12px;font-weight:500;color:var(--sc-mut);margin-top:1px;line-height:1.3}

/* ---- scent picker ----
   16px on touch widths on purpose: iOS Safari zooms the page when a select
   under 16px takes focus, and that jump on a slide cart feels broken. */
.sc-sel{appearance:none;-webkit-appearance:none;-moz-appearance:none;
display:block;width:100%;max-width:170px;margin-top:3px;
font-family:inherit;font-size:13px;font-weight:700;color:var(--sc-ink);
padding:4px 24px 4px 8px;border:1px solid var(--sc-line);border-radius:7px;
background:#fff url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23141412' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>") no-repeat right 8px center;
cursor:pointer;line-height:1.2;text-overflow:ellipsis}
.sc-sel:hover{border-color:#cdc7bc}
.sc-sel:focus{outline:2px solid var(--sc-gold);outline-offset:1px}
.sc-sel:disabled{opacity:.55;cursor:default}
.sc-sel.free{border-color:#e0cb96;background-color:#fffdf7}
@media(max-width:768px){.sc-sel{font-size:16px;padding:4px 26px 4px 8px}}
.sc-li-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:3px}
.sc-li-pr{display:flex;align-items:baseline;gap:6px;flex-wrap:wrap}
.sc-li-now{font-size:14px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.sc-li-now.free{color:var(--sc-goldd)}
.sc-li-was{font-size:12.5px;font-weight:500;color:#a39e95;text-decoration:line-through}
.sc-rmx{position:absolute;top:8px;right:0;width:22px;height:22px;border:0;background:0;cursor:pointer;
color:#a39e95;font-size:17px;line-height:1;padding:0}
.sc-rmx:hover{color:var(--sc-ink)}
.sc-qty{display:inline-flex;align-items:center;border:1.5px solid var(--sc-line);border-radius:99px;overflow:hidden;flex:none}
.sc-qty button{width:26px;height:24px;border:0;background:#fff;cursor:pointer;font-size:15px;line-height:1;color:var(--sc-ink);font-weight:600}
.sc-qty button:hover{background:var(--sc-soft)}
.sc-qty span{min-width:24px;text-align:center;font-size:13.5px;font-weight:700;font-variant-numeric:tabular-nums}

/* one-click upgrade card */
.sc-up{position:relative;margin:2px 0 10px;padding:12px 12px 12px;border-radius:12px;border:1.5px solid #e6d3a3;background:linear-gradient(180deg,#fdf8ec,#faf3e3)}
.sc-up-tag{position:absolute;top:-8px;left:10px;background:var(--sc-ink);color:#fff;font-size:9px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:4px 8px;border-radius:20px;line-height:1;display:inline-flex;align-items:center;gap:4px}
.sc-up-tag.gold{background:var(--sc-goldd)}
.sc-up-tag svg{width:9px;height:9px;fill:currentColor}
.sc-up-row{display:flex;align-items:center;gap:10px}
.sc-up-imgs{display:flex;flex:none}
.sc-up-imgs img{width:40px;height:40px;border-radius:8px;border:1px solid #ecdcb8;background:#fff;object-fit:cover}
.sc-up-imgs img+img{margin-left:-10px}
.sc-up-b{flex:1;min-width:0}
.sc-up-t{font-size:13.5px;font-weight:800;color:var(--sc-ink);letter-spacing:-.015em;line-height:1.2}
.sc-up-s{font-size:11.5px;font-weight:500;color:var(--sc-body);line-height:1.3;margin-top:2px}
.sc-up-s b{color:var(--sc-goldd);font-weight:800}
.sc-up-btn{flex:none;border:0;border-radius:8px;background:var(--sc-ink);color:#fff;font-family:inherit;font-size:12.5px;font-weight:800;letter-spacing:-.01em;padding:9px 12px;cursor:pointer;white-space:nowrap;line-height:1}
.sc-up-btn:hover{opacity:.88}
.sc-up-btn:disabled{opacity:.6;cursor:default}
.sc-up-btn s{opacity:.55;font-weight:500;margin-right:4px}

/* footer */
.sc-ft{flex:none;border-top:1px solid var(--sc-line);padding:9px 20px 11px;background:#fff;
max-height:52%;overflow-y:auto}
.sc-row{display:flex;justify-content:space-between;align-items:center;font-size:12.5px;font-weight:600;color:var(--sc-body);margin-bottom:4px}
.sc-gift{display:inline-flex;align-items:center;gap:5px;color:var(--sc-goldd);font-weight:700}
.sc-gift svg{width:11px;height:11px;fill:currentColor}
.sc-row .v{font-weight:700;color:var(--sc-ink);font-variant-numeric:tabular-nums}
.sc-row .v.was{font-weight:500;color:#a39e95;text-decoration:line-through;margin-right:6px}
.sc-row .v.free{color:var(--sc-goldd);font-weight:800}
.sc-row .v.neg{color:var(--sc-goldd);font-weight:800}
.sc-chips{display:flex;flex-wrap:wrap;gap:7px;margin:2px 0 9px}
.sc-chip{display:inline-flex;align-items:center;gap:5px;background:#f2ead9;color:#8a6a24;
font-size:11px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;padding:6px 10px;border-radius:6px;line-height:1}
.sc-chip svg{width:11px;height:11px;fill:currentColor}
.sc-sum-h{font-size:14px;font-weight:800;letter-spacing:-.015em;color:var(--sc-ink);margin:0 0 7px}
.sc-row .v.muted{font-weight:500;color:var(--sc-mut);font-size:12.5px}
.sc-row .lbl{display:inline-flex;align-items:center;gap:6px}
.sc-row .lbl svg{width:14px;height:14px;stroke:var(--sc-ink);fill:none;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.sc-info{display:inline-block;width:14px;height:14px;border:1.2px solid var(--sc-mut);border-radius:50%;font-size:9.5px;font-weight:800;line-height:12px;text-align:center;color:var(--sc-mut);margin-left:5px;vertical-align:1px}
.sc-tot{display:flex;justify-content:space-between;align-items:center;gap:10px;margin:8px 0 9px;padding:10px 13px;border-radius:10px;background:var(--sc-cream);border:1px solid #ecdcb8}
.sc-tot-l{font-size:17px;font-weight:800;letter-spacing:-.02em}
.sc-tot-r{display:inline-flex;align-items:center;gap:8px}
.sc-off{background:var(--sc-ink);color:#fff;font-size:11px;font-weight:800;letter-spacing:.04em;padding:5px 8px;border-radius:6px;line-height:1;white-space:nowrap}
.sc-tot-was{font-size:14px;font-weight:500;color:#a39e95;text-decoration:line-through;font-variant-numeric:tabular-nums}
.sc-tot-now{font-size:23px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums}
.sc-co{display:flex;align-items:center;justify-content:center;gap:9px;width:100%;border:0;border-radius:8px;padding:13px 16px;
background:#111110;color:#fff;font-family:inherit;font-size:18px;font-weight:800;letter-spacing:-.025em;cursor:pointer}
.sc-co:hover{opacity:.88}
.sc-co svg{width:16px;height:16px;stroke:#fff;fill:none;stroke-width:2.2}
.sc-trust{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:8px;align-items:start}
.sc-tr{display:flex;flex-direction:column;align-items:center;gap:5px;text-align:center}
.sc-tr svg{width:16px;height:16px;stroke:var(--sc-goldd);fill:none;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
.sc-tr span{font-size:11px;font-weight:700;color:var(--sc-body);line-height:1.32;letter-spacing:-.005em}
.sc-empty{text-align:center;padding:60px 24px}
.sc-empty p{font-size:16px;font-weight:600;color:var(--sc-mut)}
@media(max-width:400px){.sc-li-img{width:42px;height:42px}.sc-li-t{font-size:12.5px}.sc-li{gap:9px;padding:6px 0}}
@media(prefers-reduced-motion:reduce){.sc,.sc-ov{transition:none}}
`;

/* ------------------------------ build once -------------------------------- */
const TAG_SVG  = '<svg viewBox="0 0 24 24"><path d="M2 3h9l11 11-9 9L2 12V3zm4.5 5.2a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4z"/></svg>';
const LOCK_SVG = '<svg viewBox="0 0 24 24"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';

function build() {
  if (panel) return;

  if (!document.querySelector('link[data-sc-font]')) {
    const f = document.createElement('link');
    f.rel = 'stylesheet';
    f.setAttribute('data-sc-font', '1');
    f.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(f);
  }

  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);

  overlay = document.createElement('div');
  overlay.className = 'sc-ov';
  overlay.addEventListener('click', closePanel);

  panel = document.createElement('aside');
  panel.className = 'sc';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', 'Your bag');
  panel.innerHTML = `
    <div class="sc-hd">
      <h2>Your Bag <span data-sc="count"></span></h2>
      <button class="sc-x" type="button" data-sc="close" aria-label="Close">&times;</button>
    </div>
    <div class="sc-timer" data-sc="timer">
      <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
      <span data-sc="timertxt"></span>
    </div>
    <div class="sc-ship" data-sc="ship" hidden></div>
    <div class="sc-scroll">
      <div class="sc-body" data-sc="body">
        <div class="sc-lines" data-sc="lines"></div>
      </div>
      <button class="sc-more" type="button" data-sc="more" aria-label="Scroll for more">
        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
    <div class="sc-ft" data-sc="ft"></div>`;

  panel.querySelector('[data-sc="close"]').addEventListener('click', closePanel);
  panel.querySelector('.sc-body').addEventListener('click', onBodyClick);
  panel.querySelector('.sc-body').addEventListener('change', onBodyChange);

  const body = panel.querySelector('.sc-body');
  const more = panel.querySelector('[data-sc="more"]');
  if (body && more) {
    body.addEventListener('scroll', paintMore, { passive: true });
    window.addEventListener('resize', paintMore);
    more.addEventListener('click', () => {
      body.scrollBy({ top: Math.round(body.clientHeight * 0.75), behavior: 'smooth' });
    });
  }
  panel.querySelector('[data-sc="ft"]').addEventListener('click', (e) => {
    if (e.target.closest('[data-sc="checkout"]')) { window.location.href = '/checkout'; return; }
    const up = e.target.closest('[data-sc="upgrade"]');
    if (up && !busy && !swapping && !reconciling) {
      up.disabled = true; up.textContent = 'Adding\u2026';
      settle(addOne({ id: BUTTER_VARIANT, quantity: 1 }));
      return;
    }
    const b2 = e.target.closest('[data-sc="upgrade-b2g2"]');
    if (b2 && !busy && !swapping && !reconciling) {
      b2.disabled = true; b2.textContent = 'Adding\u2026';
      settle(upgradeToBundle());
    }
  });

  document.body.appendChild(overlay);
  document.body.appendChild(panel);
}

const ref = (k) => panel.querySelector(`[data-sc="${k}"]`);

/* ----------------------------- open / close ------------------------------- */
function openPanel() {
  build();
  panel.classList.add('on');
  overlay.classList.add('on');
  document.body.style.overflow = 'hidden';
  autoPass = 0;
  startReserve();
  load();
}

function closePanel() {
  if (!panel) return;
  panel.classList.remove('on');
  overlay.classList.remove('on');
  document.body.style.overflow = '';
  clearInterval(reserveTick);
}

document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePanel(); });

document.addEventListener('click', (e) => {
  const a = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
  if (a) { e.preventDefault(); openPanel(); }
}, true);

/* -------------------------------- data ------------------------------------ */
function load() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((c) => {
      cart = c;
      /* Paint straight away. Waiting on the product JSON would leave the
         drawer blank for a beat on first open. */
      render();
      return loadCartProducts().then(() => { if (cart === c) render(); });
    })
    .then(() => balance())
    .catch(() => { cart = { items: [], item_count: 0, items_subtotal_price: 0 }; render(); });
}

/* The free-bottle count is derived state, so it gets checked on every load
   rather than only after a click. However the cart changed - our stepper,
   the PDP, Horizon, a browser back-button - it lands here and gets fixed. */
function balance() {
  if (reconciling || swapping) return null;

  const paid = survey().paid;
  const want = freeFor(paid);
  const { qty } = freeState();

  if (qty === want) {
    const g = giftFix();
    if (g) { autoPass += 1; return g.then(() => load()); }
    autoPass = 0; if (trace) console.log('[sc] balanced: paid %d, free %d', paid, qty); return null;
  }
  if (autoPass >= 3) { console.warn('[sc] gave up rebalancing after 3 passes: paid %d, free %d, want %d', paid, qty, want); return null; }

  autoPass += 1;
  if (trace) console.log('[sc] rebalancing pass %d: paid %d, free %d, want %d', autoPass, paid, qty, want);
  return reconcile().then((changed) => (changed ? load() : null));
}

/* One free parfum per order while a butter is in the cart, else none.
   Returns a promise when a change is needed, null when already right. */
function giftFix() {
  if (autoPass >= 3) return null;
  const s = survey();
  const want = s.butter > 0 ? 1 : 0;
  if (s.gift === want) return null;
  if (trace) console.log('[sc] parfum: have %d, want %d', s.gift, want);
  if (s.gift === 0) return addOne({ id: GIFT_VARIANT, quantity: 1 });
  const updates = {};
  let first = true;
  for (const l of cart.items || []) {
    if (!isGiftLine(l)) continue;
    updates[l.variant_id] = (want && first) ? 1 : 0;
    first = false;
  }
  return post('/cart/update.js', { updates });
}

/* Counts AND money, in one pass. paidNet is what the bottles actually cost
   after every discount; otherNet is everything that is not a bottle, so an
   upsell in the cart never gets folded into the bundle maths. */
function survey() {
  const o = { paid: 0, free: 0, paidNet: 0, otherNet: 0, others: [], butter: 0, gift: 0 };
  for (const l of cart.items || []) {
    if (isFreeLine(l)) { o.free += l.quantity; continue; }
    if (isPaidLine(l)) { o.paid += l.quantity; o.paidNet += l.final_line_price; continue; }
    if (l.handle === BUTTER_HANDLE) o.butter += l.quantity;
    if (isGiftLine(l)) o.gift += l.quantity;
    /* One entry per non-bottle line - never merged, so a $0 gift can never
       borrow another line's title, quantity or price. */
    o.otherNet += l.final_line_price;
    o.others.push({
      title: l.product_title,
      qty:   l.quantity,
      net:   l.final_line_price,
      reg:   addonReg(l) * l.quantity,
      gift:  isGiftLine(l) || l.final_line_price === 0,
    });
  }
  o.bottles = o.paid + o.free;
  return o;
}

/* total_price is post-discount - what they actually pay. */
const netPaid = () =>
  (typeof cart.total_price === 'number' ? cart.total_price : cart.items_subtotal_price || 0);

/* ------------------------------- render ----------------------------------- */
function render() {
  if (!cart || !panel) return;

  const sub = netPaid();
  const s = survey();
  const shipFree = sub >= FREE_SHIP;
  const empty = !(cart.items || []).length;

  ref('count').textContent = cart.item_count ? `(${cart.item_count})` : '';
  paintShip(sub, empty);

  if (empty) {
    ref('lines').innerHTML = '<div class="sc-empty"><p>Your bag is empty.</p></div>';
    ref('ft').innerHTML = '';
    return;
  }

  /* Read once per render; lineHtml() uses it to decide whether a strike-
     through belongs on a bottle at all. */
  onBundle = s.free > 0;

  /* Shopify merges same-variant lines into one row with a quantity, and a
     free row has no stepper to show it - so a 2nd free bottle was invisible.
     Render one CARD per free bottle instead, while keeping the real line
     number so /cart/change.js still targets the right line. */
  const cards = [];
  (cart.items || []).forEach((l, i) => {
    const isFree = isFreeLine(l);
    if (isFree && l.quantity > 1) {
      for (let k = 0; k < l.quantity; k++) cards.push([l, i + 1, true]);
    } else {
      cards.push([l, i + 1, false]);
    }
  });
  /* Display order: paid oils, free oils, the butter and any other add-on,
     then the free parfum. Bottles read as one block and the ritual extras as
     another, instead of the butter landing in the middle of the oils. Sort is
     stable, so same-rank rows keep their cart order. The real line number
     rides along so /cart/change.js still targets the right row. */
  const rank = (l) => isGiftLine(l) ? 3 : isPaidLine(l) ? 0 : isFreeLine(l) ? 1 : 2;
  cards.sort((a, b) => rank(a[0]) - rank(b[0]));
  ref('lines').innerHTML = cards.map(([l, n, unit]) => lineHtml(l, n, unit)).join('');
  requestAnimationFrame(paintMore);

  let chips = '';
  for (const d of cart.discount_applications || []) {
    const t = d.title || d.code;
    if (t) chips += `<span class="sc-chip">${TAG_SVG}${t}</span>`;
  }

  /* ---- summary: the PDP's story, told again -------------------------------
     Every row below is additive and the column sums to Total:
       paidRegular - bundleOff + otherNet - extraOff = sub                  */

  /* Only the bundle tier quotes a regular price. The PDP's 1-Bottle row is a
     plain $49 with no strike and no save, so printing "$59 / -$10" here would
     have the two surfaces disagreeing at the one moment a customer compares
     them. Flip this only if the PDP's tier 1 starts showing a `was`. */
  const paidRegular = onBundle ? s.paid * REG_BOTTLE : s.paidNet;
  const addonReg_   = s.others.reduce((a, x) => a + x.reg, 0);
  /* Matches the PDP compare-at: REG x paid bottles (the free ones ride
     inside that number) plus the add-ons' regular prices. $118 + $39 + $19. */
  const wasTotal    = paidRegular + addonReg_;
  const saveTotal   = Math.max(0, wasTotal - sub);

  /* The breakdown: one row per thing that took money off, each a real
     number the PDP also shows, summing exactly to Subtotal - Total. */
  const bundleOff = Math.max(0, paidRegular - s.paidNet);
  const codeOff   = Math.max(0, (s.paidNet + s.otherNet) - sub);
  let breakdown = '';
  if (bundleOff) {
    const offLabel = s.free > 0 ? `Buy ${s.paid} Get ${s.free} Free` : 'Bottle discount';
    breakdown += `<div class="sc-row"><span class="sc-gift">${TAG_SVG}${offLabel}</span>` +
                 `<span class="v neg">&minus;${money(bundleOff)}</span></div>`;
  }
  for (const x of s.others) {
    const off = Math.max(0, x.reg - x.net);
    if (!off) continue;
    const name = x.gift ? 'Free ' + esc(stripFree(x.title)) : esc(x.title);
    breakdown += `<div class="sc-row"><span class="sc-gift">${TAG_SVG}${name}</span>` +
                 `<span class="v neg">&minus;${money(off)}</span></div>`;
  }
  if (codeOff) {
    breakdown += `<div class="sc-row"><span class="sc-gift">${TAG_SVG}Discount code</span>` +
                 `<span class="v neg">&minus;${money(codeOff)}</span></div>`;
  }

  /* Upgrade card: only on a gift-tier cart with no butter yet, and only
     while there is a free-shipping rule to unlock. */
  const showUp = onBundle && !s.butter && FREE_SHIP < 999999;
  const showB2 = !onBundle && s.paid === 1;
  const extra  = Math.max(0, B2G2_PRICE - sub);
  const upgrade = showB2 ?
    '<div class="sc-up"><span class="sc-up-tag">Most popular</span>' +
      '<div class="sc-up-row">' +
        `<div class="sc-up-imgs"><img src="${BOTTLE_IMGS[0]}" alt=""><img src="${BOTTLE_IMGS[1]}" alt=""></div>` +
        '<div class="sc-up-b">' +
          `<div class="sc-up-t">Get 3 more bottles for ${money(extra).replace('.00', '')}</div>` +
          '<div class="sc-up-s">Add 1 bottle, get <b>2 FREE</b> &middot; 4 bottles for $59</div>' +
        '</div>' +
        `<button type="button" class="sc-up-btn" data-sc="upgrade-b2g2">+${money(extra).replace('.00', '')}</button>` +
      '</div></div>'
    : !showUp ? '' :
    '<div class="sc-up"><span class="sc-up-tag gold">' + TAG_SVG + 'You&rsquo;ve unlocked a free gift</span>' +
      '<div class="sc-up-row">' +
        `<div class="sc-up-imgs"><img src="${BUTTER_IMG}" alt=""><img src="${PARFUM_IMG}" alt=""></div>` +
        '<div class="sc-up-b">' +
          '<div class="sc-up-t">Claim your free 10ml Parfum + free shipping</div>' +
        '</div>' +
        `<button type="button" class="sc-up-btn" data-sc="upgrade"><s>$${ADDON_REG[BUTTER_HANDLE] / 100}</s>+${money(BUTTER_PRICE).replace('.00', '')}</button>` +
      '</div></div>';

  ref('ft').innerHTML =
    upgrade +
    '<div class="sc-sum-h">Order Summary</div>' +
    breakdown +
    `<div class="sc-tot"><span class="sc-tot-l">Subtotal</span><span class="sc-tot-r">` +
      (saveTotal ? `<span class="sc-off">$${Math.round(saveTotal / 100)} OFF</span><span class="sc-tot-was">${money(wasTotal)}</span>` : '') +
      `<span class="sc-tot-now">${money(sub)}</span></span></div>` +
    /* No subscription terms. This is a one-time purchase. */
    `<button class="sc-co" type="button" data-sc="checkout">Secure Checkout ${LOCK_SVG}</button>` +
    '<div class="sc-trust">' +
      '<div class="sc-tr"><svg viewBox="0 0 24 24"><polygon points="12 2 15 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.3 12 2"/></svg>' +
        `<span>4.9 stars<br>${REVIEW_COUNT} reviews</span></div>` +
      '<div class="sc-tr"><svg viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg><span>Ships from<br>Tampa, FL</span></div>' +
      '<div class="sc-tr"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg><span>30-day<br>money back</span></div>' +
    '</div>';
}

/* The free-shipping bar under the header: how far to the threshold, or the
   unlocked state once they are over it. Hidden when the cart is empty or the
   threshold is switched off. */
const TRUCK_SVG = '<svg viewBox="0 0 24 24"><rect x="1" y="6" width="14" height="11" rx="1.5"/><path d="M15 9h4l3 3.5V17h-7z"/><circle cx="6" cy="18.5" r="1.8"/><circle cx="18" cy="18.5" r="1.8"/></svg>';
function paintShip(sub, empty) {
  const el = ref('ship');
  if (!el) return;
  if (empty || FREE_SHIP >= 999999) { el.hidden = true; return; }
  el.hidden = false;
  const left = Math.max(0, FREE_SHIP - sub);
  const pct  = Math.min(100, Math.round((sub / FREE_SHIP) * 100));
  el.className = 'sc-ship' + (left === 0 ? ' done' : '');
  el.innerHTML = TRUCK_SVG + '<div class="sc-ship-b">' +
    (left === 0
      ? '<div class="sc-ship-t">You&rsquo;ve unlocked <b>FREE SHIPPING</b></div>' +
        '<div class="sc-ship-s">Your order ships free.</div>'
      : `<div class="sc-ship-t">You&rsquo;re <b>${money(left)}</b> away from free shipping</div>`) +
    `<div class="sc-ship-bar"><div class="sc-ship-fill" style="width:${pct}%"></div></div>` +
    '</div>';
}

function lineHtml(l, lineNo, splitUnit) {
  const isGift = isGiftLine(l);
  const isFree = isFreeLine(l) || isGift;
  const img = l.image ? l.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';

  /* When a line is split into one card per bottle, prices are per unit. */
  const nowRaw = splitUnit ? l.final_price : l.final_line_price;

  /* Strike against the PDP's regular price, not the variant's list price -
     but only on the bundle tier, because the base tier quotes a plain $49. */
  const units    = splitUnit ? 1 : l.quantity;
  const isBottle = isPaidLine(l) || isFreeLine(l);
  const wasRaw   = isBottle
                 ? (onBundle ? REG_BOTTLE * units : 0)
                 : (addonReg(l) ? addonReg(l) * units
                                : (splitUnit ? l.original_price : l.original_line_price));
  const was = wasRaw > nowRaw ? wasRaw : 0;

  const price = isFree
    ? `<span class="sc-li-now free">FREE</span>${was ? `<span class="sc-li-was">${money(was)}</span>` : ''}`
    : `<span class="sc-li-now">${money(nowRaw)}</span>${was ? `<span class="sc-li-was">${money(was)}</span>` : ''}`;

  /* Free bottles are managed by the paid count, so they get no controls. */
  const qty = isFree ? '' :
    '<div class="sc-qty">' +
      `<button type="button" data-ln="${lineNo}" data-q="${l.quantity - 1}" aria-label="Decrease">&minus;</button>` +
      `<span>${l.quantity}</span>` +
      `<button type="button" data-ln="${lineNo}" data-q="${l.quantity + 1}" aria-label="Increase">+</button>` +
    '</div>';

  const rm = isFree ? '' :
    `<button class="sc-rmx" type="button" data-ln="${lineNo}" data-q="0" aria-label="Remove">&times;</button>`;

  return `<div class="sc-li${isFree ? ' free' : ''}">` +
    (isFree ? `<div class="sc-gifttag">${TAG_SVG}${isGift ? 'Your free parfum' : 'Your free bottle'}</div>` : '') +
    `<div class="sc-li-img">${img ? `<img src="${img}" alt="">` : ''}</div>` +
    '<div class="sc-li-b">' +
      `<div class="sc-li-t">${esc(isGift ? stripFree(l.product_title) : l.product_title)}</div>` +
      scentPicker(l, lineNo, splitUnit, isFree) +
      `<div class="sc-li-foot"><div class="sc-li-pr">${price}</div>${qty}</div>` +
    '</div>' + rm +
  '</div>';
}

/* Options are built from the line's OWN product, so a free row can only ever
   offer free variants and a paid row only that tier's paid variants. */
function scentPicker(l, lineNo, splitUnit, isFree) {
  const list = PRODUCTS[l.handle] || [];
  const name = l.variant_title && l.variant_title !== 'Default Title' ? l.variant_title : '';

  if (list.length < 2) {
    return name ? `<div class="sc-li-s">${esc(name)}</div>` : '';
  }

  let opts = '';
  for (const v of list) {
    opts += '<option value="' + v.id + '"' +
      (v.id === l.variant_id ? ' selected' : '') +
      (v.available ? '' : ' disabled') + '>' +
      esc(v.title) + (v.available ? '' : ' — sold out') + '</option>';
  }

  return '<select class="sc-sel' + (isFree ? ' free' : '') + '"' +
    ' data-sw-ln="' + lineNo + '"' +
    ' data-sw-vid="' + l.variant_id + '"' +
    ' data-sw-unit="' + (splitUnit ? 1 : 0) + '"' +
    ' aria-label="Change scent">' + opts + '</select>';
}

/* Shopify has no endpoint that changes a line's variant, so a swap is a
   remove then an add. Remove first, or a cart that already holds the target
   scent merges the two rows and the count is wrong before we re-add. */
function swapScent(line, lineNo, unit, newId) {
  swapping = true;

  const moveQty = unit ? 1 : line.quantity;
  const leave = line.quantity - moveQty;

  /* Mint a fresh _slot. That marker stops Shopify merging two rows holding
     the same variant; reusing it would let the new row swallow a bottle. */
  const props = {};
  const src = line.properties || {};
  for (const k in src) {
    if (Object.prototype.hasOwnProperty.call(src, k)) props[k] = src[k];
  }
  if (props._slot) {
    props._slot = 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  const add = { id: newId, quantity: moveQty };
  if (Object.keys(props).length) add.properties = props;

  return post('/cart/change.js', { line: lineNo, quantity: leave })
    .then(() => addOne(add))
    .then(() => { swapping = false; return load(); })
    .then(announce)
    .catch((err) => {
      swapping = false;
      console.error('[simone cart] scent swap failed', err);
      return load();
    });
}

/* ---------------------- entitlements -------------------------------------
   Gifts are earned by TIER, not per bottle. Nothing recurs.

     paid   free   pays    regular   per bottle
     0      0      -       -         -
     1      0      $49     -         $49.00
     2      2      $59     $118      $14.75
     3+     2      -       -         -

   Take one bottle off a B2G2 cart and both gifts go with it, because 1 paid
   sits on the base tier and the base tier has no gifts. */
function freeState() {
  const lines = (cart.items || []).filter(isFreeLine);
  return { lines, qty: lines.reduce((a, l) => a + l.quantity, 0) };
}

/* Adjust only the free-bottle count, and only by the shortfall or excess -
   never by clearing and re-adding, which would throw away their scents. */
function reconcile() {
  if (reconciling || swapping || !cart) return Promise.resolve(false);

  const paid = survey().paid;
  const want = freeFor(paid);
  const { lines, qty } = freeState();
  if (qty === want) return Promise.resolve(false);

  reconciling = true;
  let job;

  if (qty < want) {
    /* Prefer a scent they don't already have free - it reads better in the
       drawer and lands as its own line rather than bumping a quantity. */
    const held = new Set(lines.map((l) => l.variant_id));
    const fresh = SCENTS.find((x) => !held.has(x.free));
    const firstPaid = (cart.items || []).find(isPaidLine);
    const id = fresh ? fresh.free
             : (lines.length ? lines[0].variant_id
             : ((firstPaid && PAID[firstPaid.variant_id])
                  ? PAID[firstPaid.variant_id].free : SCENTS[0].free));
     job = addOne({ id, quantity: want - qty });
  } else {
    const firstPaid = (cart.items || []).find(isPaidLine);
    if (want === 0 && paid > 0 && firstPaid && firstPaid.handle === BUNDLE_HANDLE) {
      /* Fell off the bundle onto the base tier. Rebuild rather than trim,
         and keep whichever scent survived their edit. */
      if (trace) console.log('[sc] falling to base tier, resetting cart');
      job = resetToBase(firstPaid.variant_id);
    } else {
      const updates = {};
      let over = qty - want;
      for (let i = lines.length - 1; i >= 0 && over > 0; i--) {
        const take = Math.min(over, lines[i].quantity);
        updates[lines[i].variant_id] = lines[i].quantity - take;
        over -= take;
      }
      job = post('/cart/update.js', { updates });
    }
  }

  return job
    .then(() => { reconciling = false; return true; })
    .catch((e) => { reconciling = false; console.error(e); return false; });
}

/* Dropping off the gift tier is a RESET, not a subtraction.
   The B2G2 discount is already sitting on the cart. Nudging lines one at a
   time leaves Shopify recalculating a part-applied discount against a cart
   that no longer qualifies, and that is the glitch. Zero every bottle in one
   call and re-add a single base-tier one, so the cart lands in a state the
   discount engine has no opinion about.

   update.js, not clear.js: anything in the cart that is not a bottle stays. */
function resetToBase(keepVariant) {
  const updates = {};
  for (const l of cart.items || []) {
    if (isPaidLine(l) || isFreeLine(l)) updates[l.variant_id] = 0;
  }
  return post('/cart/update.js', { updates })
    .then(() => addOne({ id: keepVariant, quantity: 1 }));
}

/* Apply a cart change; load() rebalances whatever it entitles. */
function settle(p) {
  return p.then(() => load()).then(announce);
}

/* /cart/add.js is ATOMIC - one rejected line kills the whole request. Adding
   each item on its own means one failing line can never stop the gifts from
   landing. No selling_plan is ever sent: this is a one-time purchase. */
function addOne(item) {
  const plain = { id: item.id, quantity: item.quantity };
  if (item.properties) plain.properties = item.properties;

  return post('/cart/add.js', { items: [plain] }).then((res) => {
    if (res && res.description) {
      console.error('[simone cart] could not add variant ' + item.id + ':', res.description);
    }
    return res;
  });
}

/* ------------------------------ mutations --------------------------------- */
function post(url, body) {
  busy = true;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
    .then((r) => r.json())
    .then((d) => { busy = false; return d; })
    .catch((e) => { busy = false; console.error(e); });
}

function announce() {
  try {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: 'simone-slide-cart' },
    }));
  } catch (e) { /* no-op */ }
}

/* Show the chevron only when there is genuinely something below, and hide it
   once they are within 24px of the end. */
function paintMore() {
  if (!panel) return;
  const body = panel.querySelector('.sc-body');
  const wrap = panel.querySelector('.sc-scroll');
  const more = panel.querySelector('[data-sc="more"]');
  if (!body || !more || !wrap) return;

  const room = body.scrollHeight - body.clientHeight;
  const atEnd = body.scrollTop >= room - 24;
  const show = room > 24 && !atEnd;

  more.classList.toggle('on', show);
  wrap.classList.toggle('more', show);
}

function onBodyClick(e) {
  if (busy || swapping || reconciling) return;

  /* Matching on [data-q], not [data-ln]: the scent select also carries a line
     number, and on [data-ln] a click on it read as an undefined quantity. */
  const q = e.target.closest('[data-q]');
  if (q) {
    settle(post('/cart/change.js', {
      line: +q.getAttribute('data-ln'),
      quantity: +q.getAttribute('data-q'),
    }));
  }
}

function onBodyChange(e) {
  const sel = e.target.closest('select[data-sw-ln]');
  if (!sel) return;

  const oldId = +sel.getAttribute('data-sw-vid');

  /* Re-rendering replaces these nodes rather than mutating them, so a
     synthetic change means something else is driving the DOM. This is the
     guard that stopped the cart running away to 27 items. */
  if (!e.isTrusted) { sel.value = oldId; return; }
  if (busy || swapping || reconciling) { sel.value = oldId; return; }

  const lineNo = +sel.getAttribute('data-sw-ln');
  const unit = sel.getAttribute('data-sw-unit') === '1';
  const newId = +sel.value;
  if (!newId || newId === oldId) return;

  const line = (cart.items || [])[lineNo - 1];
  if (!line) { sel.value = oldId; return; }

  const list = PRODUCTS[line.handle] || [];
  let ok = false;
  for (const v of list) { if (v.id === newId) { ok = true; break; } }
  if (!ok) {
    console.error('[simone cart] refusing to swap line ' + lineNo + ' to variant ' +
      newId + ' — not a variant of ' + line.handle);
    sel.value = oldId;
    return;
  }

  sel.disabled = true;
  swapScent(line, lineNo, unit, newId);
}

/* Keep in sync when anything else in the theme changes the cart. */
document.addEventListener('cart:update', (e) => {
  if (e.detail && e.detail.sourceId === 'simone-slide-cart') return;
  autoPass = 0;
  load();
});

/* --------------------------- the custom element --------------------------- */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#stop);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#stop);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) openPanel();
  };

  /* The native dialog must never open - it would take the top layer and make
     our panel inert. Swallow the event and show ours instead. */
  #stop = (event) => {
    event.stopPropagation();
    if (typeof event.preventDefault === 'function') event.preventDefault();
    openPanel();
  };

  open()        { openPanel(); }
  close()       { closePanel(); }
  showDialog()  { openPanel(); }
  closeDialog() { closePanel(); }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

window.simoneCart = {
  open: openPanel,
  close: closePanel,
  refresh: load,
  state: () => cart,

  /* window.simoneCart.trace(true) then click the stepper. */
  trace(on = true) { trace = on; console.log('[sc] trace', on ? 'on' : 'off'); },

  /* What the drawer currently believes, and whether the footer adds up. */
  report() {
    if (!cart) { console.log('cart not loaded'); return; }
    const s = survey();
    const f = freeState();
    const sub = netPaid();
    const bundle = s.free > 0;
    const paidRegular = bundle ? s.paid * REG_BOTTLE : s.paidNet;
    const bundleOff = Math.max(0, paidRegular - s.paidNet);
    const extraOff = Math.max(0, (s.paidNet + s.otherNet) - sub);
    console.log('paid bottles :', s.paid, '| free bottles:', f.qty,
                '| entitled:', freeFor(s.paid), '| balanced:', freeFor(s.paid) === f.qty);
    console.log('reg per btl  :', money(REG_BOTTLE));
    console.log('footer       :', bottlesLabel(s.paid), money(paidRegular),
                '- bundle', money(bundleOff),
                '+ other', money(s.otherNet),
                '- extra', money(extraOff),
                '= total', money(sub));
    console.log('adds up      :', paidRegular - bundleOff + s.otherNet - extraOff === sub);
    console.log('reconciling  :', reconciling, '| swapping:', swapping, '| busy:', busy, '| autoPass:', autoPass);
    (cart.items || []).forEach((l, i) => console.log('  ' + (i + 1) + '.',
      l.variant_id, l.product_title, l.variant_title, 'x' + l.quantity,
      'final ' + l.final_line_price,
      isPaidLine(l) ? 'PAID' : (isFreeLine(l) ? 'FREE' : 'OTHER')));
    return { paid: s.paid, free: f.qty, entitled: freeFor(s.paid), total: sub };
  },
};

function bottlesLabel(n) { return n === 1 ? '1 bottle' : n + ' bottles'; }